import { fetchWithAuth } from "@/lib/fetchWithAuth"
import { supabase } from "@/integrations/supabase/client"
import type { 
  Lead, 
  Proposta, 
  Dimensionamento,
  DadosMensais, 
  GeracaoMensal, 
  AnaliseComparativa, 
  ParametrosCalculo,
  EstatisticasPropostas,
  ParametrosProjecao,
  ProjecaoCustosEconomia,
  DadosModuloFotovoltaico
} from "@/types/propostas"

// Tipos para dados do banco
interface PropostaDB {
  id: string;
  lead_id: string;
  dimensionamento_id: string;
  potencia_total_inversores: number;
  potencia_total_paineis: number;
  geracao_ano1: number;
  proposta_pdf_url: string;
  created_at: string;
  updated_at: string;
  dimensionamento_finalizado: boolean;
  lead: {
    id: string;
    nome: string;
    email: string;
    telefone: string;
    status: string;
    created_at: string;
  };
  dimensionamento?: {
    id: string;
    nome_dimensionamento: string;
    descricao_dimensionamento?: string;
  };
}

interface LeadDB {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface DadosMensaisDB {
  id: string;
  lead_id: string;
  mes: number;
  consumo: number | string;
  consumo_kwh: number;
  despesa_fatura: number | string;
  tusd: number | string;
  te: number | string;
  cosip: number | string;
  created_at: string;
  updated_at: string;
}

interface GeracaoMensalDB {
  id: string;
  lead_id: string;
  mes: number;
  geracao_kwh: number;
  created_at: string;
  updated_at: string;
}

interface AnaliseComparativaDB {
  id: string;
  lead_id: string;
  economia_mensal: number;
  economia_anual: number;
  payback_anos: number;
  created_at: string;
  updated_at: string;
}

interface ParametrosCalculoDB {
  id: string;
  lead_id: string;
  irradiacao_anual: number;
  perdas_sistema: number;
  eficiencia_inversor: number;
  created_at: string;
  updated_at: string;
}

interface EstatisticasPropostasDB {
  id: string;
  total_propostas: number;
  propostas_aprovadas: number;
  propostas_pendentes: number;
  propostas_rejeitadas: number;
  valor_total_propostas: number;
  created_at: string;
  updated_at: string;
}

interface ParametrosProjecaoDB {
  id: string;
  lead_id: string;
  inflacao_energia: number;
  inflacao_geral: number;
  taxa_desconto: number;
  created_at: string;
  updated_at: string;
}

interface ProjecaoCustosEconomiaDB {
  id: string;
  lead_id: string;
  ano: number;
  custo_energia: number;
  economia_gerada: number;
  valor_presente: number;
  created_at: string;
  updated_at: string;
}

interface DadosModuloFotovoltaicoDB {
  id: string;
  lead_id: string;
  marca: string;
  modelo: string;
  potencia: number;
  quantidade: number;
  preco_unitario: number;
  created_at: string;
  updated_at: string;
}

export class PropostasService {
  /**
   * Carrega todas as propostas com dados dos leads usando autenticação RBAC
   */
  static async carregarPropostas(): Promise<Proposta[]> {
    try {
      console.log('🔄 Carregando propostas via API backend com RBAC...');

      // Usar fetchWithAuth para respeitar políticas RBAC
      const response = await fetchWithAuth('/api/propostas', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Erro ao carregar propostas: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ ${data?.length || 0} propostas carregadas com sucesso (filtradas por RBAC)`);
      
      return data as Proposta[];
    } catch (error) {
      console.error('❌ Erro ao carregar propostas:', error)
      throw error
    }
  }

  /**
   * Carrega a análise comparativa salva de uma proposta específica
   */
  static async carregarAnaliseComparativaSalva(propostaId: string): Promise<{
    analise: AnaliseComparativa[];
    parametros: ParametrosCalculo | null;
  } | null> {
    try {
      if (!propostaId || typeof propostaId !== 'string') {
        throw new Error('ID da proposta é obrigatório e deve ser uma string');
      }

      console.log('Carregando análise comparativa salva para proposta:', propostaId);

      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('com_sfv')
        .eq('id', propostaId)
        .single();

      if (error) {
        console.error('Erro ao carregar análise comparativa:', error);
        throw new Error(`Erro ao carregar análise: ${(error as Error).message}`);
      }

      const dataTyped = data as { com_sfv?: unknown };
      if (!dataTyped || !dataTyped.com_sfv) {
        console.log('Nenhuma análise comparativa encontrada para a proposta:', propostaId);
        return null;
      }

      console.log('Análise comparativa carregada com sucesso:', dataTyped.com_sfv);

      // Converter os dados salvos para o formato AnaliseComparativa
      const dadosSalvos = dataTyped.com_sfv;
      
      // Verificar se é o formato antigo (array direto) ou novo (com parâmetros)
      let analiseSalva: AnaliseComparativa[];
      let parametrosSalvos: ParametrosCalculo | null = null;
      
      if (Array.isArray(dadosSalvos)) {
        // Formato antigo - apenas array de análise
        analiseSalva = dadosSalvos;
      } else if (dadosSalvos && (dadosSalvos as unknown as Record<string, unknown>).analise && Array.isArray((dadosSalvos as unknown as Record<string, unknown>).analise)) {
        // Formato novo - com parâmetros
        analiseSalva = (dadosSalvos as unknown as Record<string, unknown>).analise as AnaliseComparativa[];
        parametrosSalvos = (dadosSalvos as unknown as Record<string, unknown>).parametros as ParametrosCalculo || null;
      } else {
        console.log('Dados da análise comparativa estão vazios ou em formato inválido');
        return null;
      }
      
      if (analiseSalva.length === 0) {
        console.log('Array de análise está vazio');
        return null;
      }

      // Mapear os dados salvos para o formato AnaliseComparativa
      const analiseComparativa: AnaliseComparativa[] = analiseSalva.map((item: any) => ({
        mes: item.mes || '',
        consumo: item.consumo || 0,
        semSfv: item.semSfv || 0, // Carregar o valor salvo do sem SFV
        geracao: item.geracao || 0,
        instConsumido: item.instConsumido || 0,
        injetadoRede: item.injetadoRede || 0,
        consumoFaturado: item.consumoFaturado || 0,
        balancoFatura: item.balancoFatura || 0,
        creditosConsumidos: item.creditosConsumidos || 0,
        creditosAcumulados: item.creditosAcumulados || 0,
        comSfv: item.valor || item.comSfv || 0 // Usar 'valor' que é como está salvo no banco, com fallback para 'comSfv'
      }));

      // Retornar objeto com análise e parâmetros
      return {
        analise: analiseComparativa,
        parametros: parametrosSalvos
      };
    } catch (error) {
      console.error('Erro na função carregarAnaliseComparativaSalva:', error);
      throw error;
    }
  }

  /**
   * Carrega a projeção salva de uma proposta específica
   */
  static async carregarProjecaoSalva(leadId: string, dimensionamentoId?: string): Promise<ProjecaoCustosEconomia | null> {
    try {
      if (!leadId || typeof leadId !== 'string') {
        throw new Error('ID do lead é obrigatório e deve ser uma string');
      }

      console.log(`Carregando projeção salva para lead: ${leadId}${dimensionamentoId ? ` (dimensionamento: ${dimensionamentoId})` : ''}`);

      // Se temos dimensionamento_id, usar ele como filtro principal
      // Caso contrário, usar lead_id (compatibilidade com versões anteriores)
      let data, error;
      
      if (dimensionamentoId) {
      ({ data, error } = await supabase
        .from('projecao_grupob')
        .select('*')
        .eq('dimensionamento_id', dimensionamentoId)
        .order('created_at', { ascending: false })
        .limit(1));
      } else {
        ({ data, error } = await supabase
          .from('projecao_grupob')
          .select('*')
          .eq('lead_id', leadId)
          .order('created_at', { ascending: false })
          .limit(1));
      }

      if (error) {
        console.error('Erro ao carregar projeção:', error);
        throw new Error(`Erro ao carregar projeção: ${(error as Error).message}`);
      }

      if (!data || (data as unknown[]).length === 0) {
        console.log('Nenhuma projeção encontrada para o lead:', leadId);
        return null;
      }

      // Pegar a primeira (mais recente) projeção
      const projectionData = (data as unknown[])[0] as Record<string, unknown>;

      console.log('Projeção carregada com sucesso:', projectionData);

      // Usar os dados salvos diretamente da projeção
      const geracaoAno1FromProposta = (projectionData.geracao_ano1 as number) || 0;
      console.log('Valor geracao_ano1 da projeção salva:', geracaoAno1FromProposta);

      // Converter os dados salvos para o formato ProjecaoCustosEconomia
      const projecao: ProjecaoCustosEconomia = {
        parametros: {
          periodoAnalisado: (projectionData.periodo_analisado as number),
          inflacaoEnergeticaAnual: (projectionData.inflacao_energetica_anual as number) || 7.0,
          taxaDecaimentoModulos: (projectionData.taxa_decaimento_modulos as number) || 0.42
        },
        cenarioSemEnergia: {
          consumoMensalMedio: ((projectionData.consumo_anual as number) || 0) / 12,
          consumoTotalPeriodo: ((projectionData.consumo_anual as number) || 0) * ((projectionData.periodo_analisado as number) || 30),
          gastoEnergiaPeriodo: (projectionData.despesa_acumulada as number[]) && (projectionData.despesa_acumulada as number[]).length > 0
            ? (projectionData.despesa_acumulada as number[])[(projectionData.despesa_acumulada as number[]).length - 1]
            : (((projectionData.consumo_anual as number) || 0) * ((projectionData.tarifa_atual as number) || 0) + ((projectionData.cosip as number) || 0)) * ((projectionData.periodo_analisado as number) || 30),
          gastoAnualDetalhado: (projectionData.despesa as number[]) ? (projectionData.despesa as number[]).map((valor: number, index: number) => ({
            ano: index,
            valor: valor
          })) : []
        },
        cenarioComEnergia: {
          geracaoMensalAno1: geracaoAno1FromProposta / 12,
          geracaoTotalEstimada: (projectionData.geracao_acumulada as number[]) && (projectionData.geracao_acumulada as number[]).length > 0
            ? (projectionData.geracao_acumulada as number[])[(projectionData.geracao_acumulada as number[]).length - 1]
            : 0,
          geracaoAnualDetalhada: (projectionData.geracao_sistema as number[]) ? (projectionData.geracao_sistema as number[]).map((valor: number, index: number) => ({
            ano: index,
            valor: valor
          })) : [],
          despesaFaturaPeriodoComSolar: (projectionData.despesa_remanescente as number[]) && (projectionData.despesa_remanescente as number[]).length > 0
            ? (projectionData.despesa_remanescente as number[]).reduce((sum: number, valor: number) => sum + valor, 0)
            : 0
        },
        economiaTotal: (projectionData.economia_total as number) || 0,
        economiaAnualMedia: ((projectionData.economia_total as number) || 0) / ((projectionData.periodo_analisado as number) || 30),
        paybackSimples: Number(projectionData.payback_simples) || 0,
        paybackDescontado: Number(projectionData.payback_descontado) || 0,
        roeAnualizado: Number(projectionData.roe_anualizado) || 0
      };

      return projecao;
    } catch (error) {
      console.error('Erro na função carregarProjecaoSalva:', error);
      throw error;
    }
  }

  /**
   * Carrega dados mensais e geração mensal para um dimensionamento específico
   */
  static async carregarDadosMensais(leadId: string, grupoTarifario: string, dimensionamentoId?: string): Promise<{
    dadosMensais: DadosMensais[]
    geracaoMensal: GeracaoMensal
  }> {
    try {
      // Validações de entrada
      if (!leadId || typeof leadId !== 'string') {
        throw new Error('ID do lead é obrigatório e deve ser uma string');
      }
      
      if (!grupoTarifario || typeof grupoTarifario !== 'string') {
        throw new Error('Grupo tarifário é obrigatório e deve ser uma string');
      }
      
      let tabela = ''
      
      switch (grupoTarifario) {
        case 'Grupo B':
          tabela = 'clientes_grupob'
          break
        case 'Grupo A':
          tabela = 'clientes_grupoa'
          break
        case 'Mercado Livre':
          tabela = 'clientes_ml'
          break
        default:
          throw new Error(`Grupo tarifário não suportado: ${grupoTarifario}`)
      }

      console.log(`Carregando dados para lead ${leadId} do grupo ${grupoTarifario} na tabela ${tabela}${dimensionamentoId ? ` (dimensionamento: ${dimensionamentoId})` : ''}`);

      // Usar any para evitar problemas de tipo do Supabase
      let data, error;
      
      // Se temos dimensionamento_id, usar ele como filtro principal
      // Caso contrário, usar lead_id (compatibilidade com versões anteriores)
      if (dimensionamentoId) {
        ({ data, error } = await supabase
          .from(tabela)
          .select('dados_mensais, geracao_mensal')
          .eq('dimensionamento_id', dimensionamentoId)
          .single());
      } else {
        ({ data, error } = await supabase
          .from(tabela)
          .select('dados_mensais, geracao_mensal')
          .eq('lead_id', leadId)
          .single());
      }

      if (error) {
        console.error(`Erro ao consultar tabela ${tabela}:`, error);
        throw new Error(`Erro ao carregar dados: ${(error as Error).message}`);
      }

      if (!data) {
        const filtro = dimensionamentoId ? `dimensionamento ${dimensionamentoId}` : `lead ${leadId}`;
        throw new Error(`Nenhum dado encontrado para o ${filtro} na tabela ${tabela}`);
      }

      const dataTyped = data as { dados_mensais?: unknown[]; geracao_mensal?: Record<string, unknown> };
      console.log('Dados carregados com sucesso:', {
        dadosMensais: dataTyped?.dados_mensais?.length || 0,
        geracaoMensal: Object.keys(dataTyped?.geracao_mensal || {}).length
      });
      


      return {
        dadosMensais: (dataTyped?.dados_mensais as DadosMensais[]) || [],
        geracaoMensal: (dataTyped?.geracao_mensal as GeracaoMensal) || {}
      }
    } catch (error) {
      console.error('Erro ao carregar dados mensais:', error)
      throw error
    }
  }

  /**
   * Mapeia o número do mês para o nome em português
   */
  private static getMesNome(mesNum: number): string {
    const mesesMap: { [key: number]: string } = {
      1: 'janeiro',
      2: 'fevereiro', 
      3: 'marco',
      4: 'abril',
      5: 'maio',
      6: 'junho',
      7: 'julho',
      8: 'agosto',
      9: 'setembro',
      10: 'outubro',
      11: 'novembro',
      12: 'dezembro'
    }
    return mesesMap[mesNum] || 'janeiro'
  }

  /**
   * Calcula a análise comparativa para um cliente
   */
  static calcularAnaliseComparativa(
    proposta: Proposta,
    dadosMensais: DadosMensais[],
    geracaoMensal: GeracaoMensal,
    parametros: ParametrosCalculo
  ): AnaliseComparativa[] {
    try {
      // Validações de entrada
      if (!proposta) {
        throw new Error('Proposta é obrigatória');
      }
      
      if (!dadosMensais || !Array.isArray(dadosMensais) || dadosMensais.length === 0) {
        throw new Error('Dados mensais são obrigatórios e devem ser um array não vazio');
      }
      
      if (!geracaoMensal || typeof geracaoMensal !== 'object') {
        throw new Error('Geração mensal é obrigatória e deve ser um objeto');
      }
      
      if (!parametros) {
        throw new Error('Parâmetros de cálculo são obrigatórios');
      }
      
      if (typeof parametros.fatorSimultaneidade !== 'number' || parametros.fatorSimultaneidade < 0 || parametros.fatorSimultaneidade > 1) {
        throw new Error('Fator de simultaneidade deve ser um número entre 0 e 1');
      }
      
      if (typeof parametros.taxaFioB !== 'number' || parametros.taxaFioB < 0) {
        throw new Error('Taxa do Fio B deve ser um número não negativo');
      }
      
      if (!proposta.lead || !proposta.lead.padrao) {
        throw new Error('Padrão do lead é obrigatório');
      }
      if (proposta.lead.padrao === 'Múltiplas Faturas') {
        const v = proposta.lead.fatura_minima_kwh != null ? Number(proposta.lead.fatura_minima_kwh) : NaN;
        if (typeof v !== 'number' || Number.isNaN(v) || v <= 0) {
          throw new Error('Para padrão Múltiplas Faturas, a Fatura Mínima (kWh) do lead é obrigatória. Informe na etapa 1 do dimensionamento.');
        }
      }
      if (typeof proposta.potencia_total_paineis !== 'number' || proposta.potencia_total_paineis <= 0) {
        throw new Error('Potência total dos painéis deve ser um número positivo');
      }

      const meses = [
        'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
      ]

      const consumoMinimo = this.getConsumoMinimo(proposta.lead)
      let creditosAcumuladosAnterior = 0

      const analise = meses.map((mes, index) => {
        try {
          const mesNum = index + 1
          const dadosMes = dadosMensais.find(d => d.mes === mesNum)
          const mesNome = this.getMesNome(mesNum)
          const geracaoMes = geracaoMensal[mesNome] || 0

          if (!dadosMes) {
            console.warn(`Dados não encontrados para o mês ${mesNum} (${mes})`);
            return null;
          }

          // Validações dos dados mensais
          
          // Converter campos string para number se necessário
          const consumo = typeof dadosMes.consumo === 'string' ? parseFloat(dadosMes.consumo) : dadosMes.consumo;
          const despesaFatura = typeof dadosMes.despesa_fatura === 'string' ? parseFloat(dadosMes.despesa_fatura) : dadosMes.despesa_fatura;
          
          if (typeof consumo !== 'number' || isNaN(consumo) || consumo < 0) {
            console.warn(`Consumo inválido para o mês ${mes}: ${dadosMes.consumo} (tipo: ${typeof dadosMes.consumo}, convertido: ${consumo})`);
            return null;
          }
          
          if (typeof despesaFatura !== 'number' || isNaN(despesaFatura) || despesaFatura < 0) {
            console.warn(`Despesa da fatura inválida para o mês ${mes}: ${dadosMes.despesa_fatura} (tipo: ${typeof dadosMes.despesa_fatura}, convertido: ${despesaFatura})`);
            return null;
          }

          const semSfv = despesaFatura
          const geracao = geracaoMes * proposta.potencia_total_paineis
          
          // Validações das tarifas
          const te = parseFloat(dadosMes.te);
          const tusd = parseFloat(dadosMes.tusd);
          const cosip = parseFloat(dadosMes.cosip);
          
          if (isNaN(te) || isNaN(tusd) || isNaN(cosip)) {
            console.warn(`Tarifas inválidas para o mês ${mes}: TE=${dadosMes.te}, TUSD=${dadosMes.tusd}, COSIP=${dadosMes.cosip}`);
            return null;
          }
          
          const tarifaEnergia = te + tusd

          // Inst. Consumido
          const instConsumido = Math.min(
            geracao * parametros.fatorSimultaneidade,
            Math.max(consumo - consumoMinimo, 0)
          )

          // Injetado Rede
          const injetadoRede = Math.max(geracao - instConsumido, 0)

          // Consumo Faturado
          const consumoFaturado = Math.max(consumo - instConsumido, consumoMinimo)

          // Créditos Consumidos
          const creditosConsumidos = Math.min(
            injetadoRede + creditosAcumuladosAnterior,
            Math.max(consumoFaturado - consumoMinimo, 0)
          )

          // Balanço Fatura = Consumo Faturado - Créditos Consumidos (injetado mês + acumulados anterior),
          // respeitando fatura mínima. Não subtrair injetadoRede em separado pois já está em creditosConsumidos.
          const balancoFatura = Math.max(
            consumoFaturado - creditosConsumidos,
            consumoMinimo
          )

          // Créditos Acumulados
          const creditosAcumulados = Math.max(
            injetadoRede + creditosAcumuladosAnterior - creditosConsumidos,
            0
          )

          // COM SFV
          const comSfv = cosip + (consumoFaturado * tarifaEnergia) - 
                         (creditosConsumidos * tarifaEnergia * (1 - parametros.taxaFioB))

          // Atualizar créditos para o próximo mês
          creditosAcumuladosAnterior = creditosAcumulados

          return {
            mes,
            consumo,
            semSfv,
            geracao: Math.round(geracao * 100) / 100,
            instConsumido: Math.round(instConsumido * 100) / 100,
            injetadoRede: Math.round(injetadoRede * 100) / 100,
            consumoFaturado: Math.round(consumoFaturado * 100) / 100,
            balancoFatura: Math.round(balancoFatura * 100) / 100,
            creditosConsumidos: Math.round(creditosConsumidos * 100) / 100,
            creditosAcumulados: Math.round(creditosAcumulados * 100) / 100,
            comSfv: Math.round(comSfv * 100) / 100
          }
        } catch (mesError) {
          console.error(`Erro ao calcular mês ${mes}:`, mesError);
          return null;
        }
      }).filter(Boolean) as AnaliseComparativa[]

      if (analise.length === 0) {
        throw new Error('Nenhum mês foi calculado com sucesso');
      }

      // Ajuste anual: saldo de créditos de dezembro passa para janeiro; refazer cadeia Jan→Nov com
      // o novo saldo; dezembro NÃO é recalculado (evita loop).
      if (analise.length >= 12) {
        const creditosDezembro = analise[11].creditosAcumulados
        if (creditosDezembro > 0) {
          const janAtual = analise[0]
          let newJan: AnaliseComparativa
          if (janAtual.creditosAcumulados === 0) {
            // Dez > 0 e Jan = 0: recalcular janeiro com saldo de dez abatendo a conta de jan.
            const dadosMesJan = dadosMensais.find(d => d.mes === 1)
            if (!dadosMesJan) {
              newJan = { ...janAtual, creditosAcumulados: Math.round(creditosDezembro * 100) / 100 }
            } else {
              const consumo = typeof dadosMesJan.consumo === 'string' ? parseFloat(dadosMesJan.consumo) : dadosMesJan.consumo
              const te = parseFloat(dadosMesJan.te)
              const tusd = parseFloat(dadosMesJan.tusd)
              const cosip = parseFloat(dadosMesJan.cosip)
              const mesNomeJan = this.getMesNome(1)
              const geracaoJan = (geracaoMensal[mesNomeJan] || 0) * proposta.potencia_total_paineis
              const tarifaEnergia = te + tusd
              const instConsumido = Math.min(
                geracaoJan * parametros.fatorSimultaneidade,
                Math.max(consumo - consumoMinimo, 0)
              )
              const injetadoRede = Math.max(geracaoJan - instConsumido, 0)
              const consumoFaturado = Math.max(consumo - instConsumido, consumoMinimo)
              const creditosConsumidos = Math.min(
                injetadoRede + creditosDezembro,
                Math.max(consumoFaturado - consumoMinimo, 0)
              )
              const balancoFatura = Math.max(
                consumoFaturado - creditosConsumidos,
                consumoMinimo
              )
              const creditosAcumuladosJan = Math.max(
                injetadoRede + creditosDezembro - creditosConsumidos,
                0
              )
              const comSfv = cosip + (consumoFaturado * tarifaEnergia) -
                (creditosConsumidos * tarifaEnergia * (1 - parametros.taxaFioB))
              newJan = {
                ...janAtual,
                creditosConsumidos: Math.round(creditosConsumidos * 100) / 100,
                balancoFatura: Math.round(balancoFatura * 100) / 100,
                creditosAcumulados: Math.round(creditosAcumuladosJan * 100) / 100,
                comSfv: Math.round(comSfv * 100) / 100
              }
            }
          } else {
            // Jan já tem crédito > 0: somar créditos de dezembro ao acumulado de janeiro.
            newJan = {
              ...janAtual,
              creditosAcumulados: Math.round((janAtual.creditosAcumulados + creditosDezembro) * 100) / 100
            }
          }
          analise[0] = newJan

          // Recalcular Fev→Nov usando o novo saldo propagado (cada mês usa o acumulado do anterior).
          // Dezembro (index 11) não é recalculado.
          let credAnterior = newJan.creditosAcumulados
          for (let i = 1; i <= 10; i++) {
            const mes = meses[i]
            const mesNum = i + 1
            const dadosMes = dadosMensais.find(d => d.mes === mesNum)
            const mesNome = this.getMesNome(mesNum)
            const geracaoMes = geracaoMensal[mesNome] || 0
            if (!dadosMes) continue

            const consumo = typeof dadosMes.consumo === 'string' ? parseFloat(dadosMes.consumo) : dadosMes.consumo
            const te = parseFloat(dadosMes.te)
            const tusd = parseFloat(dadosMes.tusd)
            const cosip = parseFloat(dadosMes.cosip)
            const geracao = geracaoMes * proposta.potencia_total_paineis
            const tarifaEnergia = te + tusd

            const instConsumido = Math.min(
              geracao * parametros.fatorSimultaneidade,
              Math.max(consumo - consumoMinimo, 0)
            )
            const injetadoRede = Math.max(geracao - instConsumido, 0)
            const consumoFaturado = Math.max(consumo - instConsumido, consumoMinimo)
            const creditosConsumidos = Math.min(
              injetadoRede + credAnterior,
              Math.max(consumoFaturado - consumoMinimo, 0)
            )
            const balancoFatura = Math.max(
              consumoFaturado - creditosConsumidos,
              consumoMinimo
            )
            const creditosAcumulados = Math.max(
              injetadoRede + credAnterior - creditosConsumidos,
              0
            )
            const comSfv = cosip + (consumoFaturado * tarifaEnergia) -
              (creditosConsumidos * tarifaEnergia * (1 - parametros.taxaFioB))

            analise[i] = {
              mes,
              consumo,
              semSfv: analise[i].semSfv,
              geracao: Math.round(geracao * 100) / 100,
              instConsumido: Math.round(instConsumido * 100) / 100,
              injetadoRede: Math.round(injetadoRede * 100) / 100,
              consumoFaturado: Math.round(consumoFaturado * 100) / 100,
              balancoFatura: Math.round(balancoFatura * 100) / 100,
              creditosConsumidos: Math.round(creditosConsumidos * 100) / 100,
              creditosAcumulados: Math.round(creditosAcumulados * 100) / 100,
              comSfv: Math.round(comSfv * 100) / 100
            }
            credAnterior = analise[i].creditosAcumulados
          }
        }
      }

      return analise
    } catch (error) {
      console.error('Erro na função calcularAnaliseComparativa:', error);
      throw error; // Re-throw para ser tratado pelo componente
    }
  }

  /**
   * Retorna o consumo mínimo baseado no padrão do cliente.
   * Para "Múltiplas Faturas", usa lead.fatura_minima_kwh (obrigatório nesse caso).
   */
  private static getConsumoMinimo(lead: { padrao: string; fatura_minima_kwh?: number | null }): number {
    if (lead.padrao === 'Múltiplas Faturas') {
      const v = lead.fatura_minima_kwh != null ? Number(lead.fatura_minima_kwh) : NaN;
      if (typeof v !== 'number' || Number.isNaN(v) || v <= 0) return 100;
      return v;
    }
    switch (lead.padrao) {
      case 'Monofásico':
        return 30
      case 'Bifásico':
        return 50
      case 'Trifásico':
        return 100
      default:
        return 100
    }
  }

  /**
   * Salva a análise comparativa na tabela propostas_clientes
   */
  static async salvarAnaliseComparativa(
    propostaId: string, 
    analiseComparativa: AnaliseComparativa[],
    parametros: ParametrosCalculo
  ): Promise<void> {
    try {
      if (!propostaId || typeof propostaId !== 'string') {
        throw new Error('ID da proposta é obrigatório e deve ser uma string');
      }
      
      if (!analiseComparativa || !Array.isArray(analiseComparativa) || analiseComparativa.length === 0) {
        throw new Error('Análise comparativa é obrigatória e deve ser um array não vazio');
      }

      console.log('Salvando análise comparativa para proposta:', propostaId);
      console.log('Dados da análise:', analiseComparativa);

      // Preparar dados para salvar (incluindo parâmetros de cálculo)
      const dadosComSfv = analiseComparativa.map(item => ({
        mes: item.mes,
        valor: item.comSfv,
        semSfv: item.semSfv, // Salvar o valor sem SFV
        consumo: item.consumo,
        geracao: item.geracao,
        instConsumido: item.instConsumido,
        injetadoRede: item.injetadoRede,
        consumoFaturado: item.consumoFaturado,
        balancoFatura: item.balancoFatura,
        creditosConsumidos: item.creditosConsumidos,
        creditosAcumulados: item.creditosAcumulados
      }));

      // Adicionar parâmetros de cálculo aos dados salvos
      const dadosCompletos = {
        analise: dadosComSfv,
        parametros: {
          fatorSimultaneidade: parametros.fatorSimultaneidade,
          taxaFioB: parametros.taxaFioB
        }
      };

      const { error } = await supabase
        .from('propostas_clientes')
        .update({ 
          com_sfv: dadosCompletos,
          updated_at: new Date().toISOString()
        })
        .eq('id', propostaId);

      if (error) {
        console.error('Erro ao salvar análise comparativa:', error);
        throw new Error(`Erro ao salvar análise: ${(error as Error).message}`);
      }

      console.log('Análise comparativa salva com sucesso');
    } catch (error) {
      console.error('Erro na função salvarAnaliseComparativa:', error);
      throw error;
    }
  }

  /**
   * Formata o nome do cliente (nome ou razão social)
   */
  static getNomeCliente(lead: Lead | null): string {
    // Verificar se lead é null ou undefined
    if (!lead) {
      return 'Cliente sem nome'
    }

    // Se for pessoa jurídica, retornar razão social
    if (lead.tipo === 'juridica' && lead.razao_social && lead.razao_social.trim() !== '') {
      return lead.razao_social.trim()
    }
    
    // Se for pessoa física, retornar nome
    if (lead.tipo === 'fisica' && lead.nome && lead.nome.trim() !== '') {
      return lead.nome.trim()
    }
    
    // Fallback: tentar usar nome se disponível e não vazio
    if (lead.nome && lead.nome.trim() !== '') {
      return lead.nome.trim()
    }
    
    // Fallback: tentar usar razão social se disponível e não vazio
    if (lead.razao_social && lead.razao_social.trim() !== '') {
      return lead.razao_social.trim()
    }
    
    // Último fallback: usar CNPJ se disponível e não vazio
    if (lead.cnpj && lead.cnpj.trim() !== '') {
      return `CNPJ: ${lead.cnpj.trim()}`
    }
    
    return 'Cliente sem nome'
  }

  /**
   * Carrega propostas fechadas (aceitas ou de leads com status diferente de 'Lead')
   */
  static async carregarPropostasFechadas(): Promise<Proposta[]> {
    try {
      console.log('🔄 Carregando propostas fechadas...');

      // Buscar propostas aceitas
      const { data: propostasAceitas, error: errorAceitas } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          dimensionamento_id,
          potencia_total_inversores,
          potencia_total_paineis,
          geracao_ano1,
          proposta_pdf_url,
          created_at,
          updated_at,
          dimensionamento_finalizado,
          proposta_aceita,
          leads!inner (
            id,
            nome,
            tipo,
            razao_social,
            cnpj,
            cpf,
            grupo_tarifario,
            padrao,
            cidade,
            estado,
            status,
            produto
          ),
          dimensionamentos (
            id,
            nome_dimensionamento,
            descricao_dimensionamento
          )
        `)
        .eq('dimensionamento_finalizado', true)
        .eq('proposta_aceita', true)
        .order('created_at', { ascending: false });

      if (errorAceitas) {
        console.error('❌ Erro ao carregar propostas aceitas:', errorAceitas);
      }

      // Buscar propostas de leads com status diferente de 'Lead'
      const { data: propostasLeadsFechados, error: errorLeadsFechados } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          dimensionamento_id,
          potencia_total_inversores,
          potencia_total_paineis,
          geracao_ano1,
          proposta_pdf_url,
          created_at,
          updated_at,
          dimensionamento_finalizado,
          proposta_aceita,
          leads!inner (
            id,
            nome,
            tipo,
            razao_social,
            cnpj,
            cpf,
            grupo_tarifario,
            padrao,
            cidade,
            estado,
            status,
            produto
          ),
          dimensionamentos (
            id,
            nome_dimensionamento,
            descricao_dimensionamento
          )
        `)
        .eq('dimensionamento_finalizado', true)
        .neq('leads.status', 'Lead')
        .order('created_at', { ascending: false });

      if (errorLeadsFechados) {
        console.error('❌ Erro ao carregar propostas de leads fechados:', errorLeadsFechados);
      }

      // Combinar resultados e remover duplicatas
      const todasPropostas = [...(propostasAceitas || []), ...(propostasLeadsFechados || [])];
      const propostasUnicas = new Map<string, any>();
      
      todasPropostas.forEach((proposta: any) => {
        if (!propostasUnicas.has(proposta.id)) {
          propostasUnicas.set(proposta.id, proposta);
        }
      });

      // Mapear dados para o formato esperado
      const propostasMapeadas: Proposta[] = Array.from(propostasUnicas.values()).map((proposta: any) => ({
        id: proposta.id,
        lead_id: proposta.lead_id,
        dimensionamento_id: proposta.dimensionamento_id,
        potencia_total_inversores: proposta.potencia_total_inversores,
        potencia_total_paineis: proposta.potencia_total_paineis,
        geracao_ano1: proposta.geracao_ano1,
        proposta_pdf_url: proposta.proposta_pdf_url,
        created_at: proposta.created_at,
        updated_at: proposta.updated_at,
        dimensionamento_finalizado: proposta.dimensionamento_finalizado,
        lead: proposta.leads || {
          id: proposta.lead_id,
          nome: 'Cliente sem nome',
          tipo: 'fisica',
          razao_social: '',
          cnpj: '',
          cpf: '',
          grupo_tarifario: 'Grupo B',
          padrao: 'Trifásico',
          cidade: '',
          estado: '',
          status: ''
        },
        dimensionamento: proposta.dimensionamentos || null
      }));

      console.log(`✅ ${propostasMapeadas.length} propostas fechadas carregadas com sucesso`);
      return propostasMapeadas;
    } catch (error) {
      console.error('❌ Erro ao carregar propostas fechadas:', error);
      return [];
    }
  }

  /**
   * Calcula estatísticas das propostas
   */
  static calcularEstatisticas(propostas: Proposta[]): EstatisticasPropostas {
    const totalPropostas = propostas.length
    const potenciaTotal = propostas.reduce((acc, p) => acc + (p.potencia_total_inversores || 0), 0)
    const potenciaPaineis = propostas.reduce((acc, p) => acc + (p.potencia_total_paineis || 0), 0)
    const mediaPorSistema = totalPropostas > 0 ? potenciaTotal / totalPropostas : 0

    return {
      totalPropostas,
      potenciaTotal: potenciaTotal.toFixed(2),
      potenciaPaineis: potenciaPaineis.toFixed(2),
      mediaPorSistema: mediaPorSistema.toFixed(2)
    }
  }

  // Buscar dados do cliente para projeção de custos
  static async buscarDadosCliente(leadId: string, grupoTarifario: string, propostaId: string) {
    try {
      console.log('Buscando dados do cliente:', { leadId, grupoTarifario, propostaId });
      
      // Determinar a tabela baseada no grupo tarifário
      let tabela = '';
      switch (grupoTarifario) {
        case 'Grupo B':
          tabela = 'clientes_grupob';
          break;
        case 'Grupo A':
          tabela = 'clientes_grupoa';
          break;
        case 'Mercado Livre':
          tabela = 'clientes_ml';
          break;
        default:
          throw new Error(`Grupo tarifário não suportado: ${grupoTarifario}`);
      }

      // Buscar dados do cliente na tabela apropriada
      const { data, error } = await supabase
        .from(tabela)
        .select('dados_mensais, geracao_mensal')
        .eq('lead_id', leadId)
        .single();

      if (error) {
        console.error(`Erro ao consultar tabela ${tabela}:`, error);
        throw new Error(`Erro ao carregar dados do cliente: ${(error as Error).message}`);
      }

      const dataTyped = data as { dados_mensais?: unknown[]; geracao_mensal?: Record<string, unknown> };
      if (!dataTyped || !dataTyped.dados_mensais || !Array.isArray(dataTyped.dados_mensais)) {
        throw new Error(`Dados mensais não encontrados para o lead ${leadId} na tabela ${tabela}`);
      }

      // Calcular consumo anual e despesa anual a partir dos dados mensais
      const dadosMensais = dataTyped.dados_mensais;
      let consumoAnual = 0;
      let despesaFaturaAnual = 0;

      dadosMensais.forEach((mes: DadosMensaisDB) => {
        // Converter campos string para number se necessário
        const consumo = typeof mes.consumo === 'string' ? parseFloat(mes.consumo) : mes.consumo;
        const despesaFatura = typeof mes.despesa_fatura === 'string' ? parseFloat(mes.despesa_fatura) : mes.despesa_fatura;
        
        if (typeof consumo === 'number' && !isNaN(consumo) && consumo >= 0) {
          consumoAnual += consumo;
        }
        
        if (typeof despesaFatura === 'number' && !isNaN(despesaFatura) && despesaFatura >= 0) {
          despesaFaturaAnual += despesaFatura;
        }
      });

      // Buscar geração anual da tabela propostas_clientes
      let geracaoAnual = 0;
      if (propostaId) {
        try {
        const { data: propostaData, error: propostaError } = await supabase
          .from('propostas_clientes')
          .select('potencia_total_paineis')
          .eq('id', propostaId)
          .single();

          if (propostaError) {
            console.warn('Erro ao buscar dados da proposta para geração:', propostaError);
          } else if (propostaData) {
            const propostaTyped = propostaData as { potencia_total_paineis?: number };
            if (propostaTyped.potencia_total_paineis) {
              // Calcular geração anual estimada baseada na potência dos painéis
              // Assumindo uma geração média de 1.200 kWh/kWp/ano (valor típico para o Brasil)
              const geracaoMediaKwp = 1200; // kWh/kWp/ano
              geracaoAnual = propostaTyped.potencia_total_paineis * geracaoMediaKwp;
              console.log('Geração anual calculada da potência dos painéis:', {
                potenciaPaineis: propostaTyped.potencia_total_paineis,
                geracaoMediaKwp,
                geracaoAnual
              });
            }
          }
        } catch (error) {
          console.warn('Erro ao buscar dados da proposta:', error);
        }
      }

      // Se não conseguiu calcular a geração da proposta, tentar usar dados de irradiação
      if (geracaoAnual === 0) {
        if (dataTyped.geracao_mensal && typeof dataTyped.geracao_mensal === 'object') {
          const geracaoMensal = dataTyped.geracao_mensal;
          Object.values(geracaoMensal).forEach((valor: unknown) => {
            const geracao = typeof valor === 'string' ? parseFloat(valor) : valor;
            if (typeof geracao === 'number' && !isNaN(geracao) && geracao >= 0) {
              geracaoAnual += geracao;
            }
          });
        }
      }

      // Se ainda não houver dados de geração, usar um valor padrão baseado no consumo
      if (geracaoAnual === 0) {
        geracaoAnual = consumoAnual * 1.2; // Estimativa: geração 20% maior que o consumo
        console.log('Usando estimativa padrão para geração anual:', geracaoAnual);
      }

      console.log('Dados do cliente calculados:', {
        consumoAnual,
        despesaFaturaAnual,
        geracaoAnual
      });

      return {
        consumoAnual,
        despesaFaturaAnual,
        geracaoAnual
      };
    } catch (error) {
      console.error('Erro ao buscar dados do cliente:', error);
      throw error;
    }
  }

  // Método auxiliar para buscar dados básicos do cliente
  private static async buscarDadosBasicosCliente(leadId: string, grupoTarifario: string) {
    // Determinar a tabela baseada no grupo tarifário
    let tabela = '';
    switch (grupoTarifario) {
      case 'Grupo B':
        tabela = 'clientes_grupob';
        break;
      case 'Grupo A':
        tabela = 'clientes_grupoa';
        break;
      case 'Mercado Livre':
        tabela = 'clientes_ml';
        break;
      default:
        throw new Error(`Grupo tarifário não suportado: ${grupoTarifario}`);
    }

    // Buscar dados do cliente na tabela apropriada
    const { data, error } = await supabase
      .from(tabela)
      .select('dados_mensais, geracao_mensal')
      .eq('lead_id', leadId)
      .single();

    if (error) {
      console.error(`Erro ao consultar tabela ${tabela}:`, error);
        throw new Error(`Erro ao carregar dados do cliente: ${(error as Error).message}`);
    }

    const dataTyped = data as { dados_mensais?: unknown[]; geracao_mensal?: Record<string, unknown> };
    if (!dataTyped || !dataTyped.dados_mensais || !Array.isArray(dataTyped.dados_mensais)) {
      throw new Error(`Dados mensais não encontrados para o lead ${leadId} na tabela ${tabela}`);
    }

    // Calcular consumo anual e despesa anual a partir dos dados mensais
    const dadosMensais = dataTyped.dados_mensais;
    let consumoAnual = 0;
    let despesaFaturaAnual = 0;

    dadosMensais.forEach((mes: DadosMensaisDB) => {
      // Converter campos string para number se necessário
      const consumo = typeof mes.consumo === 'string' ? parseFloat(mes.consumo) : mes.consumo;
      const despesaFatura = typeof mes.despesa_fatura === 'string' ? parseFloat(mes.despesa_fatura) : mes.despesa_fatura;
      
      if (typeof consumo === 'number' && !isNaN(consumo) && consumo >= 0) {
        consumoAnual += consumo;
      }
      
      if (typeof despesaFatura === 'number' && !isNaN(despesaFatura) && despesaFatura >= 0) {
        despesaFaturaAnual += despesaFatura;
      }
    });

    // Calcular geração anual estimada (se disponível)
    let geracaoAnual = 0;
    if (dataTyped.geracao_mensal && typeof dataTyped.geracao_mensal === 'object') {
      const geracaoMensal = dataTyped.geracao_mensal;
      Object.values(geracaoMensal).forEach((valor: unknown) => {
        const geracao = typeof valor === 'string' ? parseFloat(valor) : valor;
        if (typeof geracao === 'number' && !isNaN(geracao) && geracao >= 0) {
          geracaoAnual += geracao;
        }
      });
    }

    // Se não houver dados de geração, usar um valor padrão baseado no consumo
    if (geracaoAnual === 0) {
      geracaoAnual = consumoAnual * 1.2; // Estimativa: geração 20% maior que o consumo
    }

    console.log('Dados básicos do cliente calculados:', {
      consumoAnual,
      despesaFaturaAnual,
      geracaoAnual
    });

    return {
      consumoAnual,
      despesaFaturaAnual,
      geracaoAnual
    };
  }

  // Calcular projeção de custos e economia usando API Python
  static async calcularProjecaoCustosEconomiaAPI(
    leadId: string,
    parametros: ParametrosProjecao,
    dimensionamentoId?: string
  ): Promise<ProjecaoCustosEconomia> {
    try {
      console.log('Chamando API Python para calcular projeção...');
      
      const response = await fetchWithAuth('/api/projecao-grupob', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          lead_id: leadId,
          dimensionamento_id: dimensionamentoId,
          parametros_frontend: {
            periodoAnalisado: parametros.periodoAnalisado,
            inflacaoEnergeticaAnual: parametros.inflacaoEnergeticaAnual,
            taxaDecaimentoModulos: parametros.taxaDecaimentoModulos
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} ${response.statusText}`);
      }

      const resultado = await response.json();
      
      if (resultado.erro) {
        throw new Error(resultado.erro);
      }

      console.log('Projeção calculada pela API Python:', resultado);

      // Buscar o valor de geracao_ano1 da tabela propostas_clientes
      let geracaoAno1FromProposta = 0;
      try {
        // Se temos dimensionamento_id, usar ele como filtro principal
        // Caso contrário, usar lead_id (compatibilidade com versões anteriores)
        let propostaData, propostaError;
        
        if (dimensionamentoId) {
          ({ data: propostaData, error: propostaError } = await supabase
            .from('propostas_clientes')
            .select('geracao_ano1')
            .eq('dimensionamento_id', dimensionamentoId)
            .single() as unknown as { data: { geracao_ano1?: number } | null; error: unknown | null });
        } else {
          // Se não há dimensionamentoId, buscar a proposta mais recente do lead
          ({ data: propostaData, error: propostaError } = await supabase
            .from('propostas_clientes')
            .select('geracao_ano1')
            .eq('lead_id', leadId)
            .order('created_at', { ascending: false })
            .limit(1)
            .single() as unknown as { data: { geracao_ano1?: number } | null; error: unknown | null });
        }

        if (!propostaError && propostaData) {
          const propostaTyped = propostaData as { geracao_ano1?: number };
          if (propostaTyped.geracao_ano1) {
            geracaoAno1FromProposta = propostaTyped.geracao_ano1;
            console.log('Valor geracao_ano1 encontrado na proposta:', geracaoAno1FromProposta);
          } else {
            console.log('Valor geracao_ano1 não encontrado na proposta, usando valor da API');
            geracaoAno1FromProposta = (resultado.cliente as { geracao_ano1?: number }).geracao_ano1 || 0;
          }
        } else {
          console.log('Valor geracao_ano1 não encontrado na proposta, usando valor da API');
          geracaoAno1FromProposta = (resultado.cliente as { geracao_ano1?: number }).geracao_ano1 || 0;
        }
      } catch (error) {
        console.warn('Erro ao buscar geracao_ano1 da proposta:', error);
        geracaoAno1FromProposta = resultado.cliente.geracao_ano1 || 0;
      }

      // Mapear os dados da API para o formato esperado pelo frontend
      const consumoMensalMedio = resultado.cliente.consumo_anual / 12;
      const despesaMensalMedia = (resultado.cliente.consumo_anual * resultado.cliente.tarifa_atual + resultado.cliente.cosip) / 12;

      return {
        parametros,
        cenarioSemEnergia: {
          consumoMensalMedio: consumoMensalMedio,
          consumoTotalPeriodo: resultado.cliente.consumo_anual * parametros.periodoAnalisado,
          gastoEnergiaPeriodo: resultado.projecao_anos && resultado.projecao_anos.length > 0
            ? resultado.projecao_anos[resultado.projecao_anos.length - 1].despesa_acumulada
            : (resultado.cliente.consumo_anual * resultado.cliente.tarifa_atual + resultado.cliente.cosip) * parametros.periodoAnalisado,
          gastoAnualDetalhado: resultado.projecao_anos.slice(1).map((ano: Record<string, unknown>) => ({
            ano: ano.ano as number,
            valor: ano.despesa as number
          }))
        },
        cenarioComEnergia: {
          geracaoMensalAno1: geracaoAno1FromProposta / 12,
          geracaoTotalEstimada: resultado.projecao_anos && resultado.projecao_anos.length > 0
            ? resultado.projecao_anos[resultado.projecao_anos.length - 1].geracao_acumulada as number
            : 0,
          geracaoAnualDetalhada: resultado.projecao_anos.slice(1).map((ano: Record<string, unknown>) => ({
            ano: ano.ano as number,
            valor: ano.geracao_sistema as number
          })),
          despesaFaturaPeriodoComSolar: resultado.projecao_anos && resultado.projecao_anos.length > 0
            ? resultado.projecao_anos.reduce((sum: number, ano: Record<string, unknown>) => sum + ((ano.despesa_remanescente as number) || 0), 0)
            : 0
        },
        economiaTotal: resultado.indicadores_financeiros.economia_total,
        economiaAnualMedia: resultado.indicadores_financeiros.economia_total / parametros.periodoAnalisado,
        paybackSimples: Number(resultado.indicadores_financeiros.payback_simples) || 0,
        paybackDescontado: Number(resultado.indicadores_financeiros.payback_descontado) || 0,
        roeAnualizado: Number(resultado.indicadores_financeiros.roe_anualizado) || 0
      };
      
    } catch (error) {
      console.error('Erro ao chamar API Python:', error);
      throw error;
    }
  }

  // Buscar módulos fotovoltaicos dos kits salvos na proposta do cliente
  // IMPORTANTE: Busca apenas o módulo do kit1, conforme solicitado
  static async buscarModulosKitsSalvos(propostaId: string): Promise<DadosModuloFotovoltaico[]> {
    try {
      // Validar se o ID da proposta é válido
      if (!propostaId || propostaId.trim() === '') {
        console.log('ID da proposta é vazio ou inválido, retornando array vazio');
        return [];
      }

      console.log('Buscando módulos dos kits salvos para proposta:', propostaId);
      
      // Buscar os dados do kit1 (incluindo insumos para kits manuais)
      const { data: proposta, error: propostaError } = await supabase
        .from('propostas_clientes')
        .select(`
          kit1_modulo_descricao,
          kit1_insumos,
          kit1_codigo
        `)
        .eq('id', propostaId)
        .single();

      if (propostaError) {
        console.error('Erro ao buscar proposta:', propostaError);
        throw new Error(`Erro ao buscar proposta: ${(propostaError as Error).message}`);
      }

      if (!proposta) {
        console.log('Nenhuma proposta encontrada:', propostaId);
        return [];
      }

      console.log('Dados do kit1 encontrados:', proposta);

      const propostaData = proposta as Record<string, unknown>;
      let descricaoModulo: string | null = null;

      // Verificar se é kit manual (kit1_codigo começa com "manual-")
      const kit1Codigo = propostaData.kit1_codigo as string | null;
      const isKitManual = kit1Codigo && kit1Codigo.startsWith('manual-');

      if (isKitManual) {
        // Para kits manuais, buscar a descrição do módulo em kit1_insumos
        console.log('Kit manual detectado, buscando módulo em kit1_insumos');
        const kit1Insumos = propostaData.kit1_insumos as Record<string, unknown> | null;
        
        if (kit1Insumos && kit1Insumos.Insumos && Array.isArray(kit1Insumos.Insumos)) {
          // Procurar o primeiro insumo do tipo "PAINEL" ou "Painel Solar"
          const insumoPainel = (kit1Insumos.Insumos as Array<Record<string, unknown>>).find(
            (insumo) => {
              const tipo = String(insumo.Tipo || '').toUpperCase();
              return tipo === 'PAINEL' || tipo === 'PAINEL SOLAR';
            }
          );
          
          if (insumoPainel) {
            // 1. Tentar buscar por Código se for UUID (Kits Manuais atualizados)
            const codigoPainel = insumoPainel.Codigo as string | null;
            if (codigoPainel && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(codigoPainel)) {
              console.log(`Buscando módulo por UUID: ${codigoPainel}`);
              const { data: moduloUUID, error: errorUUID } = await supabase
                .from('modulos_fotovoltaicos')
                .select('marca, modelo, potencia, tecnologia')
                .eq('id', codigoPainel)
                .single();
              
              if (!errorUUID && moduloUUID) {
                console.log(`✅ Módulo encontrado por UUID:`, moduloUUID);
                return [{
                  marca: (moduloUUID.marca as string) || 'Não especificada',
                  modelo: (moduloUUID.modelo as string) || 'Modelo não especificado',
                  potencia: (moduloUUID.potencia as number) || 0,
                  tecnologia: (moduloUUID.tecnologia as string) || 'Tecnologia não especificada',
                  codigo: propostaId,
                  encontrado: true,
                  descricaoOriginal: (insumoPainel.Descricao as string) || ''
                }];
              }
            }

            // 2. Fallback para descrição se não encontrar por UUID
            if (insumoPainel.Descricao) {
              descricaoModulo = insumoPainel.Descricao as string;
              console.log(`Descrição do módulo extraída de kit1_insumos: "${descricaoModulo}"`);
            }
          }
        }
      } else {
        // Para kits Aldo, priorizar kit1_modulo_descricao
        descricaoModulo = propostaData.kit1_modulo_descricao as string | null;
        
        // Se estiver vazio, tentar extrair de kit1_insumos (compatibilidade)
        if (!descricaoModulo || descricaoModulo.trim() === '') {
          console.log('kit1_modulo_descricao vazio, tentando extrair de kit1_insumos para kit Aldo');
          const kit1Insumos = propostaData.kit1_insumos as Record<string, unknown> | null;
          if (kit1Insumos && kit1Insumos.Insumos && Array.isArray(kit1Insumos.Insumos)) {
            const insumoPainel = (kit1Insumos.Insumos as Array<Record<string, unknown>>).find(
              (insumo) => {
                const tipo = String(insumo.Tipo || '').toLowerCase();
                return tipo.includes('painel');
              }
            );
            if (insumoPainel && insumoPainel.Descricao) {
              descricaoModulo = insumoPainel.Descricao as string;
              console.log(`Descrição do módulo extraída de kit1_insumos: "${descricaoModulo}"`);
            }
          }
        } else {
          console.log(`Kit Aldo detectado, usando kit1_modulo_descricao: "${descricaoModulo}"`);
        }
      }

      // Se não encontrou descrição, retornar array vazio
      if (!descricaoModulo || descricaoModulo.trim() === '') {
        console.log('Nenhuma descrição de módulo encontrada para o kit1 da proposta:', propostaId);
        return [];
      }

      // Buscar o módulo na tabela modulos_fotovoltaicos
      try {
        // Extrair o modelo principal da descrição
        // Para kits manuais: "Marca Modelo" -> usar o modelo completo (sem a marca)
        // Para kits Aldo: descrição completa do módulo, usar primeira parte antes do espaço
        let modeloBusca: string;
        
        if (isKitManual) {
          // Para kits manuais, a descrição é "Marca Modelo"
          // Exemplo: "DAH DHN-I66Z16/DG/FS-610W" -> buscar por "DHN-I66Z16/DG/FS-610W"
          const partes = descricaoModulo.split(' ');
          // Se tiver mais de uma palavra, usar tudo exceto a primeira (marca)
          // Caso contrário, usar tudo
          modeloBusca = partes.length > 1 ? partes.slice(1).join(' ') : descricaoModulo;
        } else {
          // Para kits Aldo, usar a primeira parte antes do espaço
          // Exemplo: "ODA585-36V-MHD 585W..." -> buscar por "ODA585-36V-MHD"
          modeloBusca = descricaoModulo.split(' ')[0];
        }
        
        console.log(`Buscando módulo para modelo: "${modeloBusca}" da descrição: "${descricaoModulo}"`);
        
        // Buscar o módulo na tabela modulos_fotovoltaicos usando o modelo
        const { data: moduloCompleto, error: moduloError } = await supabase
          .from('modulos_fotovoltaicos')
          .select('marca, modelo, potencia, tecnologia')
          .ilike('modelo', `%${modeloBusca}%`)
          .limit(1);

        if (moduloError) {
          console.warn(`Erro ao buscar módulo para descrição "${descricaoModulo}":`, moduloError);
          return [{
            marca: 'Não especificada',
            modelo: descricaoModulo,
            potencia: 0,
            tecnologia: 'Tecnologia não especificada',
            codigo: propostaId,
            encontrado: false,
            descricaoOriginal: descricaoModulo
          }];
        }

        if (moduloCompleto && (moduloCompleto as unknown[]).length > 0) {
          const modulo = moduloCompleto[0];
          console.log(`✅ Módulo encontrado para descrição "${descricaoModulo}":`, modulo);
          return [{
            marca: (modulo.marca as string) || 'Não especificada',
            modelo: (modulo.modelo as string) || 'Modelo não especificado',
            potencia: (modulo.potencia as number) || 0,
            tecnologia: (modulo.tecnologia as string) || 'Tecnologia não especificada',
            codigo: propostaId,
            encontrado: true,
            descricaoOriginal: descricaoModulo
          }];
        } else {
        console.log(`❌ Módulo não encontrado para modelo: "${modeloBusca}", tentando busca alternativa`);
        
        // Tentar extrair potência da descrição como fallback
        let potenciaFallback = 0;
        const potenciaMatch = descricaoModulo.match(/(\d+)\s*W[P]?/i);
        if (potenciaMatch) {
          potenciaFallback = parseInt(potenciaMatch[1]);
        }

        // Tentar busca alternativa: se for kit manual e não encontrou, tentar buscar pela marca também
        if (isKitManual) {
          const partes = descricaoModulo.split(' ');
          if (partes.length > 1) {
            const marca = partes[0];
            const modelo = partes.slice(1).join(' ');
            
            // Tentar buscar por marca e modelo
            const { data: moduloAlt, error: erroAlt } = await supabase
              .from('modulos_fotovoltaicos')
              .select('marca, modelo, potencia, tecnologia')
              .ilike('marca', `%${marca}%`)
              .ilike('modelo', `%${modelo}%`)
              .limit(1);
            
            if (!erroAlt && moduloAlt && moduloAlt.length > 0) {
              const modulo = moduloAlt[0];
              console.log(`✅ Módulo encontrado com busca alternativa:`, modulo);
              return [{
                marca: (modulo.marca as string) || 'Não especificada',
                modelo: (modulo.modelo as string) || 'Modelo não especificado',
                potencia: (modulo.potencia as number) || 0,
                tecnologia: (modulo.tecnologia as string) || 'Tecnologia não especificada',
                codigo: propostaId,
                encontrado: true,
                descricaoOriginal: descricaoModulo
              }];
            }
          }
        }
        
        console.log(`❌ Módulo não encontrado após tentativas, usando dados básicos com potência extraída: ${potenciaFallback}W`);
        return [{
          marca: isKitManual ? (descricaoModulo.split(' ')[0] || 'Não especificada') : 'Não especificada',
          modelo: isKitManual ? (descricaoModulo.split(' ').slice(1).join(' ') || descricaoModulo) : descricaoModulo,
          potencia: potenciaFallback,
          tecnologia: 'Tecnologia não especificada',
          codigo: propostaId,
          encontrado: false,
          descricaoOriginal: descricaoModulo
        }];
        }
      } catch (error) {
        console.warn(`Erro ao processar módulo "${descricaoModulo}":`, error);
        return [{
          marca: 'Não especificada',
          modelo: descricaoModulo,
          potencia: 0,
          tecnologia: 'Tecnologia não especificada',
          codigo: propostaId,
          encontrado: false,
          descricaoOriginal: descricaoModulo
        }];
      }
      
    } catch (error) {
      console.error('Erro ao buscar módulos dos kits salvos:', error);
      throw error;
    }
  }

  // Buscar módulo fotovoltaico por código específico
  static async buscarModuloPorCodigo(codigo: string): Promise<DadosModuloFotovoltaico | null> {
    try {
      if (!codigo || codigo.trim() === '') {
        return null;
      }

      console.log('Buscando módulo por código:', codigo);

      // Primeiro, tentar buscar por código exato na tabela modulos_fotovoltaicos
      // Como não temos campo código na tabela, vamos tentar por modelo
      const { data, error } = await supabase
        .from('modulos_fotovoltaicos')
        .select('marca, modelo, potencia, tecnologia')
        .ilike('modelo', `%${codigo}%`)
        .limit(1);

      if (error) {
        console.warn('Erro ao buscar módulo por código:', error);
        return null;
      }

      if (data && (data as unknown[]).length > 0) {
        const modulo = data[0];
        return {
          marca: (modulo.marca as string) || 'Não especificada',
          modelo: (modulo.modelo as string) || 'Modelo não especificado',
          potencia: (modulo.potencia as number) || 0,
          tecnologia: (modulo.tecnologia as string) || 'Tecnologia não especificada',
          encontrado: true,
          descricaoOriginal: codigo
        };
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar módulo por código:', error);
      return null;
    }
  }

  /**
   * Deleta apenas a URL do PDF da proposta, mantendo todos os outros dados
   */
  static async deletarApenasPdfProposta(propostaId: string): Promise<void> {
    try {
      if (!propostaId || typeof propostaId !== 'string') {
        throw new Error('ID da proposta é obrigatório e deve ser uma string');
      }

      console.log('🔍 FUNÇÃO CORRETA: Removendo apenas a URL do PDF da proposta:', propostaId);
      console.log('🔍 Esta função NÃO deve deletar dados de dimensionamento ou etapas anteriores');

      // Atualizar a proposta removendo apenas a URL do PDF
      const { error: updateError } = await supabase
        .from('propostas_clientes')
        .update({
          proposta_pdf_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', propostaId);

      if (updateError) {
        console.error('❌ Erro ao atualizar proposta:', updateError);
        throw new Error(`Erro ao remover URL do PDF: ${(updateError as Error).message}`);
      }

      console.log('✅ URL do PDF removida com sucesso da proposta:', propostaId);
      console.log('✅ Todos os outros dados da proposta foram MANTIDOS');
    } catch (error) {
      console.error('❌ Erro na função deletarApenasPdfProposta:', error);
      throw error;
    }
  }

  /**
   * Deleta apenas a projeção de custos e economia de um dimensionamento específico, mantendo todos os outros dados
   */
  static async deletarProjecao(dimensionamentoId: string): Promise<void> {
    try {
      if (!dimensionamentoId || typeof dimensionamentoId !== 'string') {
        throw new Error('ID do dimensionamento é obrigatório e deve ser uma string');
      }

      console.log('🗑️ Iniciando deleção da projeção de custos e economia para dimensionamento:', dimensionamentoId);
      console.log('✅ Todos os outros dados serão MANTIDOS');

      // Deletar apenas os registros da projeção na tabela projecao_grupob usando dimensionamento_id
      const { error } = await supabase
        .from('projecao_grupob')
        .delete()
        .eq('dimensionamento_id', dimensionamentoId);

      if (error) {
        console.error('❌ Erro ao deletar projeção:', error);
        throw new Error(`Erro ao deletar projeção: ${(error as Error).message}`);
      }

      console.log('✅ Projeção de custos e economia deletada com sucesso do dimensionamento:', dimensionamentoId);
      console.log('✅ Todos os outros dados foram MANTIDOS intactos');
    } catch (error) {
      console.error('❌ Erro na função deletarProjecao:', error);
      throw error;
    }
  }

  /**
   * Deleta apenas a análise comparativa da proposta, mantendo todos os outros dados
   */
  static async deletarAnaliseComparativa(propostaId: string): Promise<void> {
    try {
      if (!propostaId || typeof propostaId !== 'string') {
        throw new Error('ID da proposta é obrigatório e deve ser uma string');
      }

      console.log('🗑️ Iniciando deleção da análise comparativa para proposta:', propostaId);
      console.log('✅ Todos os outros dados da proposta serão MANTIDOS');

      // Atualizar apenas o campo com_sfv para null, mantendo todos os outros dados
      const { error } = await supabase
        .from('propostas_clientes')
        .update({
          com_sfv: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', propostaId);

      if (error) {
        console.error('❌ Erro ao deletar análise comparativa:', error);
        throw new Error(`Erro ao deletar análise comparativa: ${(error as Error).message}`);
      }

      console.log('✅ Análise comparativa deletada com sucesso da proposta:', propostaId);
      console.log('✅ Todos os outros dados da proposta foram MANTIDOS intactos');
    } catch (error) {
      console.error('❌ Erro na função deletarAnaliseComparativa:', error);
      throw error;
    }
  }

  /**
   * Deleta uma proposta e todos os dados relacionados
   */
  static async deletarProposta(propostaId: string): Promise<void> {
    try {
      if (!propostaId || typeof propostaId !== 'string') {
        throw new Error('ID da proposta é obrigatório e deve ser uma string');
      }

      console.log('Iniciando deleção da proposta:', propostaId);

      // 1. Buscar dados da proposta para obter lead_id e grupo tarifário
      const { data: proposta, error: propostaError } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          leads (
            id,
            grupo_tarifario
          )
        `)
        .eq('id', propostaId)
        .single();

      if (propostaError || !proposta) {
        throw new Error(`Erro ao buscar proposta: ${(propostaError as Error)?.message || 'Proposta não encontrada'}`);
      }

      const propostaTyped = proposta as { lead_id: string; leads?: { grupo_tarifario?: string } };
      const leadId = propostaTyped.lead_id;
      const grupoTarifario = propostaTyped.leads?.grupo_tarifario;

      console.log('Dados da proposta encontrados:', { propostaId, leadId, grupoTarifario });

      // 2. Deletar projeções relacionadas
      console.log('Deletando projeções relacionadas...');
      const { error: projecaoError } = await supabase
        .from('projecao_grupob')
        .delete()
        .eq('lead_id', leadId);

      if (projecaoError) {
        console.warn('Erro ao deletar projeções (continuando):', projecaoError);
      } else {
        console.log('Projeções deletadas com sucesso');
      }

      // 3. Deletar dados do grupo tarifário se existir
      if (grupoTarifario && leadId) {
        let tabelaGrupo = '';
        switch (grupoTarifario) {
          case 'Grupo B':
            tabelaGrupo = 'clientes_grupob';
            break;
          case 'Grupo A':
            tabelaGrupo = 'clientes_grupoa';
            break;
          case 'Mercado Livre':
            tabelaGrupo = 'clientes_ml';
            break;
          default:
            console.log('Grupo tarifário não reconhecido para deleção:', grupoTarifario);
        }

        if (tabelaGrupo) {
          console.log(`Deletando dados da tabela ${tabelaGrupo}...`);
          const { error: grupoError } = await supabase
            .from(tabelaGrupo)
            .delete()
            .eq('lead_id', leadId);

          if (grupoError) {
            console.warn(`Erro ao deletar dados de ${tabelaGrupo} (continuando):`, grupoError);
          } else {
            console.log(`Dados de ${tabelaGrupo} deletados com sucesso`);
          }
        }
      }

      // 4. Deletar a proposta principal
      console.log('Deletando proposta principal...');
      const { error: deleteError } = await supabase
        .from('propostas_clientes')
        .delete()
        .eq('id', propostaId);

      if (deleteError) {
        throw new Error(`Erro ao deletar proposta: ${(deleteError as Error).message}`);
      }

      console.log('Proposta deletada com sucesso:', propostaId);
    } catch (error) {
      console.error('Erro na função deletarProposta:', error);
      throw error;
    }
  }

  /**
   * Valida se todos os dados necessários para gerar a proposta estão disponíveis
   * Esta é uma validação básica para permitir acesso à seção de geração
   */
  static async validarDadosParaGeracao(propostaId: string): Promise<{
    podeGerar: boolean;
    mensagemErro?: string;
    dadosFaltantes: string[];
  }> {
    try {
      const dadosFaltantes: string[] = [];
      console.log('🔍 Iniciando validação para proposta:', propostaId);

      // 1. Verificar se a proposta existe e tem dados básicos
      console.log('🔍 Passo 1: Verificando dados básicos da proposta...');
      const { data: proposta, error: propostaError } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          dimensionamento_id,
          potencia_total_inversores,
          potencia_total_paineis,
          geracao_ano1,
          leads (
            id,
            nome,
            tipo,
            razao_social,
            grupo_tarifario,
            padrao,
            cidade,
            estado
          )
        `)
        .eq('id', propostaId)
        .single();

      if (propostaError || !proposta) {
        console.log('❌ Dados básicos da proposta não encontrados:', propostaError);
        dadosFaltantes.push('Dados básicos da proposta não encontrados');
        return {
          podeGerar: false,
          mensagemErro: 'Proposta não encontrada ou dados básicos incompletos.',
          dadosFaltantes
        };
      }
      
      // Verificar se proposta tem as propriedades esperadas
      if (!proposta || typeof proposta !== 'object') {
        console.log('❌ Proposta inválida');
        dadosFaltantes.push('Proposta inválida');
        return {
          podeGerar: false,
          mensagemErro: 'Proposta inválida.',
          dadosFaltantes
        };
      }
      
      const propostaData = proposta as Record<string, unknown>;
      console.log('✅ Dados básicos da proposta OK:', {
        id: propostaData.id,
        lead_id: propostaData.lead_id,
        potencia_inversores: propostaData.potencia_total_inversores,
        potencia_paineis: propostaData.potencia_total_paineis,
        geracao_ano1: propostaData.geracao_ano1
      });

      // 2. Verificar dados do lead
      console.log('🔍 Passo 2: Verificando dados do lead...');
      if (!propostaData.leads) {
        console.log('❌ Dados do cliente (lead) não encontrados');
        dadosFaltantes.push('Dados do cliente (lead) não encontrados');
      } else {
        const lead = propostaData.leads as Record<string, unknown>;
        console.log('📋 Dados do lead encontrados:', {
          nome: lead.nome,
          razao_social: lead.razao_social,
          grupo_tarifario: lead.grupo_tarifario,
          padrao: lead.padrao,
          cidade: lead.cidade,
          estado: lead.estado
        });

        if (!lead.nome && !lead.razao_social) {
          dadosFaltantes.push('Nome do cliente não informado');
        }
        if (!lead.grupo_tarifario) {
          dadosFaltantes.push('Grupo tarifário não informado');
        }
        if (!lead.padrao) {
          dadosFaltantes.push('Padrão de tensão não informado');
        }
        if (!lead.cidade || !lead.estado) {
          dadosFaltantes.push('Cidade e estado não informados');
        }
      }

      // 3. Verificar especificações técnicas
      console.log('🔍 Passo 3: Verificando especificações técnicas...');
      if (!propostaData.potencia_total_inversores || (propostaData.potencia_total_inversores as number) <= 0) {
        console.log('❌ Potência total dos inversores não informada');
        dadosFaltantes.push('Potência total dos inversores não informada');
      }
      if (!propostaData.potencia_total_paineis || (propostaData.potencia_total_paineis as number) <= 0) {
        console.log('❌ Potência total dos painéis não informada');
        dadosFaltantes.push('Potência total dos painéis não informada');
      }
      if (!propostaData.geracao_ano1 || (propostaData.geracao_ano1 as number) <= 0) {
        console.log('❌ Geração anual estimada não calculada');
        dadosFaltantes.push('Geração anual estimada não calculada');
      }

      // 4. Verificar análise comparativa salva
      console.log('🔍 Passo 4: Verificando análise comparativa salva...');
      const analiseComparativa = await this.carregarAnaliseComparativaSalva(propostaId);
      console.log('📊 Resultado da análise comparativa:', {
        existe: !!analiseComparativa,
        temAnalise: !!(analiseComparativa?.analise),
        quantidadeMeses: analiseComparativa?.analise?.length || 0
      });

      if (!analiseComparativa || !analiseComparativa.analise || analiseComparativa.analise.length === 0) {
        console.log('❌ Análise comparativa não foi calculada e salva');
        dadosFaltantes.push('Análise comparativa deve ser calculada e salva');
      }

      // 5. Verificar projeção de custos
      console.log('🔍 Passo 5: Verificando projeção de custos...');
      const projecaoCustos = await this.carregarProjecaoSalva(propostaData.lead_id as string, propostaData.dimensionamento_id as string);
      console.log('📈 Resultado da projeção de custos:', {
        existe: !!projecaoCustos,
        parametros: projecaoCustos?.parametros
      });

      if (!projecaoCustos) {
        console.log('❌ Projeção de custos e economia não foi calculada e salva');
        dadosFaltantes.push('Projeção de custos e economia deve ser calculada e salva');
      }

      // 6. Verificar dados mensais e geração mensal
      console.log('🔍 Passo 6: Verificando dados mensais e geração mensal...');
      if ((propostaData.leads as Record<string, unknown>)?.grupo_tarifario) {
        const lead = propostaData.leads as Record<string, unknown>;
        const dadosMensais = await this.carregarDadosMensais(propostaData.lead_id as string, (lead.grupo_tarifario as string), propostaData.dimensionamento_id as string);
        console.log('📅 Resultado dos dados mensais:', {
          dadosMensais: dadosMensais.dadosMensais?.length || 0,
          geracaoMensal: Object.keys(dadosMensais.geracaoMensal || {}).length
        });

        if (!dadosMensais.dadosMensais || dadosMensais.dadosMensais.length === 0) {
          console.log('❌ Dados mensais de consumo não encontrados');
          dadosFaltantes.push('Dados mensais de consumo não encontrados');
        }
        if (!dadosMensais.geracaoMensal || Object.keys(dadosMensais.geracaoMensal).length === 0) {
          console.log('❌ Dados de geração mensal não calculados');
          dadosFaltantes.push('Dados de geração mensal não calculados');
        }
      }

      // 7. Verificar módulos fotovoltaicos
      console.log('🔍 Passo 7: Verificando módulos fotovoltaicos...');
      const modulos = await this.buscarModulosKitsSalvos(propostaId);
      console.log('🔋 Resultado dos módulos fotovoltaicos:', {
        quantidade: modulos?.length || 0,
        modulos: modulos?.map(m => ({ marca: m.marca, modelo: m.modelo, encontrado: m.encontrado }))
      });

      if (!modulos || modulos.length === 0) {
        console.log('❌ Módulos fotovoltaicos não foram configurados na proposta');
        dadosFaltantes.push('Módulos fotovoltaicos devem ser configurados na proposta');
      } else {
        // Verificar se pelo menos um módulo foi encontrado corretamente
        const modulosValidos = modulos.filter(m => m.encontrado && m.potencia > 0);
        if (modulosValidos.length === 0) {
          console.log('❌ Nenhum módulo válido encontrado - apenas descrições básicas salvas');
          dadosFaltantes.push('Nenhum módulo fotovoltaico válido encontrado. Configure módulos válidos na proposta.');
        } else {
          console.log(`✅ ${modulosValidos.length} módulos válidos encontrados`);
        }
      }

      // Resultado da validação
      console.log('📋 Resumo da validação:', {
        totalProblemas: dadosFaltantes.length,
        problemas: dadosFaltantes
      });

      if (dadosFaltantes.length === 0) {
        console.log('✅ Validação aprovada - todos os dados necessários estão disponíveis');
        return {
          podeGerar: true,
          dadosFaltantes: []
        };
      } else {
        const mensagemErro = `Não é possível gerar a proposta. Faltam as seguintes informações:\n\n${dadosFaltantes.map(item => `• ${item}`).join('\n')}\n\nComplete essas informações antes de gerar a proposta.`;
        console.log('❌ Validação reprovada - dados faltantes:', dadosFaltantes);

        return {
          podeGerar: false,
          mensagemErro,
          dadosFaltantes
        };
      }

    } catch (error) {
      console.error('❌ Erro ao validar dados para geração da proposta:', error);
      return {
        podeGerar: false,
        mensagemErro: 'Erro interno ao validar dados. Tente novamente.',
        dadosFaltantes: ['Erro na validação dos dados']
      };
    }
  }

  /**
   * Busca dados da projeção para mapear variáveis do PDF
   */
  static async buscarDadosProjecaoParaPdf(leadId: string): Promise<{
    geracaoTotal: number;
    energiaTotal: number;
    consumoTotal: number;
    consumoRem: number;
    investimento: number;
    despesaTotal: number;
    despesaRem: number;
    despesaNova: number;
    economiaTotal: number;
    economiaFatura: number;
    manutencao: number;
    payback1: number;
    tir1: number;
    lucroLiquido: number;
    periodoAnos: number;
    inflacao: number;
  } | null> {
    try {
      console.log('Buscando dados da projeção para PDF do lead:', leadId);

      const { data, error } = await supabase
        .from('projecao_grupob')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Erro ao buscar dados da projeção:', error);
        return null;
      }

      if (!data || (data as unknown[]).length === 0) {
        console.log('Nenhuma projeção encontrada para o lead:', leadId);
        return null;
      }

      // Pegar a primeira (mais recente) projeção
      const projectionData = (data as unknown[])[0];
      console.log('Dados da projeção encontrados:', projectionData);

      // Extrair dados dos arrays JSONB
      const projectionTyped = projectionData as {
        geracao_acumulada?: number[];
        consumo_acumulado?: number[];
        despesa_acumulada?: number[];
        despesa_remanescente?: number[];
        manutencao?: number[];
        economia_acumulada?: number[];
        resultado_acumulado?: number[];
        consumo_remanescente?: number[];
        investimento?: number;
        payback_simples?: number;
        roe_anualizado?: number;
        periodo_analisado?: number;
        inflacao_energetica_anual?: number;
      };
      
      const geracaoAcumulada = projectionTyped.geracao_acumulada || [];
      const consumoAcumulado = projectionTyped.consumo_acumulado || [];
      const despesaAcumulada = projectionTyped.despesa_acumulada || [];
      const despesaRemanescente = projectionTyped.despesa_remanescente || [];
      const manutencao = projectionTyped.manutencao || [];
      const economiaAcumulada = projectionTyped.economia_acumulada || [];
      const resultadoAcumulado = projectionTyped.resultado_acumulado || [];

      // Calcular valores
      const ultimoAno = geracaoAcumulada.length - 1;
      
      // GeracaoTotal → projecao_grupob.geracao_acumulada (último ano)
      const geracaoTotal = geracaoAcumulada[ultimoAno] || 0;
      
      // EnergiaTotal → divisão entre geracao_acumulada e consumo_acumulado (último ano)
      const energiaTotal = consumoAcumulado[ultimoAno] > 0 
        ? (geracaoAcumulada[ultimoAno] / consumoAcumulado[ultimoAno]) * 100 
        : 0;
      
      // ConsumoTotal → projecao_grupob.consumo_acumulado (último ano)
      const consumoTotal = consumoAcumulado[ultimoAno] || 0;
      
      // ConsumoRem → projecao_grupob.consumo_remanescente (último ano)
      const consumoRem = (projectionTyped.consumo_remanescente || [])[ultimoAno] || 0;
      
      // Investimento → projecao_grupob.investimento
      const investimento = projectionTyped.investimento || 0;
      
      // DespesaTotal → projecao_grupob.despesa_acumulada (último ano)
      const despesaTotal = despesaAcumulada[ultimoAno] || 0;
      
      // DespesaRem → projecao_grupob.despesa_remanescente (soma todos os anos)
      const despesaRem = despesaRemanescente.reduce((sum, valor) => sum + (valor || 0), 0);
      
      // Manutencao → projecao_grupob.manutencao (soma todos os anos)
      const manutencaoTotal = manutencao.reduce((sum, valor) => sum + (valor || 0), 0);
      
      // DespesaNova → despesa_remanescente + manutencao (soma todos os anos)
      const despesaNova = despesaRem + manutencaoTotal;
      
      // EconomiaTotal → projecao_grupob.economia_acumulada (último ano)
      const economiaTotal = economiaAcumulada[ultimoAno] || 0;
      
      // EconomiaFatura → mesmo que EconomiaTotal
      const economiaFatura = economiaTotal;
      
      // Payback1 → projecao_grupob.payback_simples
      const payback1 = projectionTyped.payback_simples || 0;
      
      // TIR1 → projecao_grupob.roe_anualizado
      const tir1 = projectionTyped.roe_anualizado || 0;
      
      // LucroLiquido → projecao_grupob.resultado_acumulado (último ano)
      const lucroLiquido = resultadoAcumulado[ultimoAno] || 0;
      
      // PeriodoAnos → projecao_grupob.periodo_analisado
      const periodoAnos = projectionTyped.periodo_analisado || 0;
      
      // Inflacao → projecao_grupob.inflacao_energetica_anual
      const inflacao = projectionTyped.inflacao_energetica_anual || 0;

      const dadosMapeados = {
        geracaoTotal,
        energiaTotal,
        consumoTotal,
        consumoRem,
        investimento,
        despesaTotal,
        despesaRem,
        despesaNova,
        economiaTotal,
        economiaFatura,
        manutencao: manutencaoTotal,
        payback1,
        tir1,
        lucroLiquido,
        periodoAnos,
        inflacao
      };

      console.log('Dados mapeados para PDF:', dadosMapeados);
      return dadosMapeados;

    } catch (error) {
      console.error('Erro ao buscar dados da projeção para PDF:', error);
      return null;
    }
  }

  /**
   * Validação rigorosa para geração da proposta - chamada apenas quando usuário clica em "Gerar"
   */
  static async validarDadosParaGeracaoEstrita(propostaId: string): Promise<{
    podeGerar: boolean;
    mensagemErro?: string;
    dadosFaltantes: string[];
  }> {
    try {
      const dadosFaltantes: string[] = [];
      console.log('🔒 Iniciando validação ESTRICTA para geração da proposta:', propostaId);

      // 1. Verificar se a proposta existe e tem dados básicos
      console.log('🔍 Passo 1: Verificando dados básicos da proposta...');
      const { data: proposta, error: propostaError } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          dimensionamento_id,
          potencia_total_inversores,
          potencia_total_paineis,
          geracao_ano1,
          leads (
            id,
            nome,
            tipo,
            razao_social,
            grupo_tarifario,
            padrao,
            cidade,
            estado
          )
        `)
        .eq('id', propostaId)
        .single();

      if (propostaError || !proposta) {
        dadosFaltantes.push('Proposta não encontrada ou dados básicos incompletos');
        return {
          podeGerar: false,
          mensagemErro: 'Proposta não encontrada ou dados básicos incompletos.',
          dadosFaltantes
        };
      }

      // 2. Verificar dados essenciais do lead
      const propostaData = proposta as Record<string, unknown>;
      if (!propostaData.leads) {
        dadosFaltantes.push('Dados do cliente não encontrados');
      } else {
        const lead = propostaData.leads as Record<string, unknown>;
        if (!lead.nome && !lead.razao_social) {
          dadosFaltantes.push('Nome ou razão social do cliente não informado');
        }
        if (!lead.grupo_tarifario) {
          dadosFaltantes.push('Grupo tarifário não informado');
        }
        if (!lead.padrao) {
          dadosFaltantes.push('Padrão de tensão não informado');
        }
        if (!lead.cidade || !lead.estado) {
          dadosFaltantes.push('Cidade e estado não informados');
        }
      }

      // 3. Verificar especificações técnicas obrigatórias
      if (!propostaData.potencia_total_inversores || (propostaData.potencia_total_inversores as number) <= 0) {
        dadosFaltantes.push('Potência total dos inversores não informada ou inválida');
      }
      if (!propostaData.potencia_total_paineis || (propostaData.potencia_total_paineis as number) <= 0) {
        dadosFaltantes.push('Potência total dos painéis não informada ou inválida');
      }
      if (!propostaData.geracao_ano1 || (propostaData.geracao_ano1 as number) <= 0) {
        dadosFaltantes.push('Geração anual estimada não calculada ou inválida');
      }

      // 4. Verificar análise comparativa OBRIGATÓRIA para geração
      console.log('🔍 Verificando análise comparativa (OBRIGATÓRIA)...');
      const analiseComparativa = await this.carregarAnaliseComparativaSalva(propostaId);
      if (!analiseComparativa || !analiseComparativa.analise || analiseComparativa.analise.length === 0) {
        dadosFaltantes.push('Análise comparativa deve ser calculada e salva antes de gerar a proposta');
      }

      // 5. Verificar projeção de custos OBRIGATÓRIA para geração
      console.log('🔍 Verificando projeção de custos (OBRIGATÓRIA)...');
      const projecaoCustos = await this.carregarProjecaoSalva(propostaData.lead_id as string, propostaData.dimensionamento_id as string);
      if (!projecaoCustos) {
        dadosFaltantes.push('Projeção de custos e economia deve ser calculada e salva antes de gerar a proposta');
      }

      // 6. Verificar dados mensais OBRIGATÓRIOS
      if ((propostaData.leads as Record<string, unknown>)?.grupo_tarifario) {
        const lead = propostaData.leads as Record<string, unknown>;
        const dadosMensais = await this.carregarDadosMensais(propostaData.lead_id as string, (lead.grupo_tarifario as string), propostaData.dimensionamento_id as string);
        if (!dadosMensais.dadosMensais || dadosMensais.dadosMensais.length === 0) {
          dadosFaltantes.push('Dados mensais de consumo não encontrados');
        }
        if (!dadosMensais.geracaoMensal || Object.keys(dadosMensais.geracaoMensal).length === 0) {
          dadosFaltantes.push('Dados de geração mensal não calculados');
        }
      }

      // 7. Verificar módulos fotovoltaicos OBRIGATÓRIOS
      const modulos = await this.buscarModulosKitsSalvos(propostaId);
      if (!modulos || modulos.length === 0) {
        dadosFaltantes.push('Pelo menos um módulo fotovoltaico deve ser configurado na proposta');
      } else {
        // Verificar se há pelo menos um módulo válido
        const modulosValidos = modulos.filter(m => m.encontrado && m.potencia > 0);
        if (modulosValidos.length === 0) {
          dadosFaltantes.push('Nenhum módulo fotovoltaico válido encontrado. Configure módulos válidos na proposta.');
        }
      }

      // Resultado da validação rigorosa
      if (dadosFaltantes.length === 0) {
        console.log('✅ Validação rigorosa aprovada - todos os dados necessários estão disponíveis');
        return { podeGerar: true, dadosFaltantes: [] };
      } else {
        const mensagemErro = `Não é possível gerar a proposta. Os seguintes dados são obrigatórios:\n\n${dadosFaltantes.map(item => `• ${item}`).join('\n')}\n\nComplete todas as etapas anteriores antes de gerar a proposta.`;
        console.log('❌ Validação rigorosa reprovada - dados obrigatórios faltantes:', dadosFaltantes);

        return {
          podeGerar: false,
          mensagemErro,
          dadosFaltantes
        };
      }

    } catch (error) {
      console.error('❌ Erro na validação rigorosa:', error);
      return {
        podeGerar: false,
        mensagemErro: 'Erro interno ao validar dados. Tente novamente.',
        dadosFaltantes: ['Erro na validação dos dados']
      };
    }
  }

  /**
   * Marca o dimensionamento como não finalizado quando há alterações
   * @param leadId - ID do lead
   * @param dimensionamentoId - ID do dimensionamento específico (opcional, se não fornecido marca todos)
   */
  static async marcarDimensionamentoNaoFinalizado(leadId: string, dimensionamentoId?: string): Promise<void> {
    try {
      console.log('🔄 Marcando dimensionamento como não finalizado para lead:', leadId, 'dimensionamento:', dimensionamentoId);
      
      let error;
      
      if (dimensionamentoId) {
        // Se dimensionamentoId for fornecido, filtrar apenas por ele
        const result = await (supabase as any)
          .from('propostas_clientes')
          .update({ 
            dimensionamento_finalizado: false,
            updated_at: new Date().toISOString()
          })
          .eq('lead_id', leadId)
          .eq('dimensionamento_id', dimensionamentoId);
        
        error = result.error;
      } else {
        // Se não for fornecido, marcar todos os dimensionamentos do lead
        const result = await (supabase as any)
          .from('propostas_clientes')
          .update({ 
            dimensionamento_finalizado: false,
            updated_at: new Date().toISOString()
          })
          .eq('lead_id', leadId);
        
        error = result.error;
      }

      if (error) {
        console.error('❌ Erro ao marcar dimensionamento como não finalizado:', error);
        throw new Error(`Erro ao atualizar status do dimensionamento: ${(error as Error).message}`);
      }

      const target = dimensionamentoId ? `dimensionamento específico (${dimensionamentoId})` : 'todos os dimensionamentos';
      console.log(`✅ ${target} marcado como não finalizado com sucesso`);
    } catch (error) {
      console.error('❌ Erro na função marcarDimensionamentoNaoFinalizado:', error);
      throw error;
    }
  }
}
